import { useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Mail } from "lucide-react";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email, name, source: "newsletter_page" });
      setDone(true);
      toast.success("You're on the list.");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <SEO
        title="The Field Notes Newsletter"
        description="Twice-monthly leadership notes from the fireground — short, honest, no fluff. Tools and quiet leadership ideas from Robbie South."
      />
      <section className="bg-brand-ink text-white">
        <div className="container-narrow py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="overline text-brand-rust">Field Notes from South on Leadership</span>
            <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[1.05]">
              Leadership notes — <em className="italic text-white/85">straight from the fireground.</em>
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              Twice-monthly. Short. Honest. No fluff. Tools, scripts, and the quiet leadership ideas Robbie is working on right now — sent the day they're written.
            </p>
            <ul className="mt-8 space-y-2 text-white/75">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-rust" /> Drawn from real fireground and boardroom moments</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-rust" /> A first look at upcoming books, courses, and talks</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-rust" /> One click to unsubscribe — always</li>
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-brand-ink p-8 md:p-10 border border-brand-line">
              {done ? (
                <div className="text-center py-6" data-testid="newsletter-success">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-brand-rust" />
                  <h3 className="font-display text-3xl mt-4">You're on the list.</h3>
                  <p className="text-brand-inkMuted mt-3">First Field Note arrives this week.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="newsletter-form">
                  <div className="flex items-center gap-2 text-brand-rust"><Mail className="w-5 h-5" /><span className="overline">Subscribe — free</span></div>
                  <h2 className="font-display text-3xl text-brand-ink">Join 2,400 leaders.</h2>
                  <div>
                    <Input data-testid="newsletter-name" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-brand-line focus:border-brand-ink focus:ring-0" />
                  </div>
                  <div>
                    <Input data-testid="newsletter-email" type="email" required placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-brand-line focus:border-brand-ink focus:ring-0" />
                  </div>
                  <button type="submit" disabled={busy} data-testid="newsletter-submit" className="w-full bg-brand-ink text-white hover:bg-black py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-60">
                    {busy ? "Subscribing…" : "Subscribe"}
                  </button>
                  <p className="text-xs text-brand-inkMuted text-center">No spam. Twice a month. Unsubscribe anytime.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
